import { useInfiniteQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { createFileRoute, useRouteContext } from '@tanstack/react-router';
import { ConfigProvider, Empty, Flex, Grid, Result, message } from 'antd';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  getListDatabaseSearchTabsQueryKey,
  getSearchDatabasePeopleUrl,
  searchDatabasePeople,
  updateDatabaseSearchTab,
  useGetDatabaseFilters,
  useListDatabaseSearchTabs,
  useTouchDatabaseSearchTab,
} from '../../../../api';
import { useCommunicationCapabilities, usePermissions } from '../../../../modules/account/hooks';
import { useAiUiSnapshot } from '../../../../modules/ai-next/hooks';
import { useAiAssistant } from '../../../../modules/ai-next/providers';
import { SmsForm } from '../../../../modules/communications/components/sms-form';
import { EmailForm } from '../../../../modules/connection/components/email-form';
import {
  DatabaseDrawer,
  DatabaseSearchError,
  DatabaseSearchTabs,
  DatabaseTable,
  RevealJobToast,
  RevealPopover,
} from '../../../../modules/database/components';
import { REVEAL_COUNT_MAX, REVEAL_EXCLUDE_MAX } from '../../../../modules/database/constants';
import { useCompanyFirstSearch } from '../../../../modules/database/hooks';
import { canonicalFiltersKey } from '../../../../modules/database/utils';
import { GlobalSearch } from '../../../../modules/global-search/components';
import { HeaderActions } from '../../../../modules/navigation/components/header-actions';
import { SelectionActionsBar } from '../../../../modules/navigation/components/selection-actions-bar';
import { AutosaveIndicator } from '../../../../ui/components/autosave-indicator';
import { ColumnPickerButton } from '../../../../ui/components/column-picker-button';
import { useTableSelection } from '../../../../ui/components/data-grid';
import { FloatingWindow } from '../../../../ui/components/floating-window';
import { Loader } from '../../../../ui/components/loader';
import { TableSurface } from '../../../../ui/components/table-surface';
import {
  useDensity,
  useFlyToTarget,
  useNavigationPosition,
  useUndoableSave,
} from '../../../../ui/hooks';
import { useLayoutInsets } from '../../../../ui/providers/layout-insets';
import { storage } from '../../../../ui/utils/local-storage';

import type {
  BulkRecipientsBodyDto,
  DatabaseSearchResponseDto,
  DatabaseSearchResponseDtoRowsItem,
  DatabaseSearchTabDto,
  ListDatabaseSearchTabsQueryResult,
} from '../../../../api';
import type {
  DatabaseTableProps,
  RevealPopoverProps,
} from '../../../../modules/database/components';

const DatabaseSearchRoute = () => {
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { findVisible, fly } = useFlyToTarget();

  const insets = useLayoutInsets();
  const isMobile = Grid.useBreakpoint().md === false;
  const { isSideNav } = useNavigationPosition();
  const { density, toolbarTheme } = useDensity('grid');
  const { panel, uiContext } = useAiAssistant();
  const { selectedOrganization, selectedMembership } = useRouteContext({
    from: '/_authenticated/_app',
  });

  const { searchId } = Route.useParams();
  const { can } = usePermissions();
  const { canSendEmail, canSendSms, connections, emailConnection } = useCommunicationCapabilities();
  const [compose, setCompose] = useState<{
    channel: 'email' | 'sms';
    target: BulkRecipientsBodyDto;
  } | null>(null);
  const canSearch = can('database', 'search');
  const canReveal = can('database', 'reveal');
  const [drawer, setDrawer] = useState<{
    open: boolean;
    row: DatabaseSearchResponseDtoRowsItem;
    view: 'person' | 'company';
  } | null>(null);

  const tabsQuery = useListDatabaseSearchTabs(selectedOrganization.id, {
    query: { enabled: canSearch },
  });
  const filtersQuery = useGetDatabaseFilters(selectedOrganization.id, {
    query: { enabled: canSearch },
  });
  const openTabs = tabsQuery.data?.data.open;
  const activeTab = openTabs?.find((tab) => tab.id === searchId);

  useEffect(() => {
    if (openTabs && !activeTab && !tabsQuery.isFetching) {
      navigate({ to: '/database', replace: true });
    }
  }, [openTabs, activeTab, tabsQuery.isFetching, navigate]);

  const sort = activeTab?.sort ?? null;
  const [columnLayout, setColumnLayout] = useState(() =>
    storage.getJSON<DatabaseTableProps['columnLayout']>('k3x_database_columns', {}),
  );
  const appliedTabRef = useRef<DatabaseSearchTabDto | undefined>(undefined);
  const tabSave = useUndoableSave<
    DatabaseSearchTabDto & { columns: DatabaseTableProps['columnLayout'] }
  >({
    apply: ({ columns, ...next }) => {
      appliedTabRef.current = next;
      setColumnLayout(columns);
      storage.setJSON('k3x_database_columns', columns);
      queryClient.setQueryData<ListDatabaseSearchTabsQueryResult>(
        getListDatabaseSearchTabsQueryKey(selectedOrganization.id),
        (old) =>
          old && {
            ...old,
            data: {
              ...old.data,
              open: old.data.open.map((tab) => (tab.id === next.id ? next : tab)),
            },
          },
      );
    },
    save: ({ id, filters, sort: nextSort }) =>
      updateDatabaseSearchTab(selectedOrganization.id, id, { filters, sort: nextSort }),
    onError: () => message.error('Failed to save search'),
    debounceMs: 0,
  });

  const { track: trackTab } = tabSave;
  const previousTabRef = useRef(activeTab);
  useEffect(() => {
    const previous = previousTabRef.current;
    previousTabRef.current = activeTab;
    if (
      previous &&
      activeTab &&
      previous.id === activeTab.id &&
      activeTab !== appliedTabRef.current &&
      !isEqual([previous.filters, previous.sort], [activeTab.filters, activeTab.sort])
    ) {
      trackTab({ ...previous, columns: columnLayout }, { ...activeTab, columns: columnLayout });
    }
  }, [activeTab, trackTab, columnLayout]);

  const current = activeTab && { ...activeTab, columns: columnLayout };
  const handleSortChange = (next: { field: string; direction: 'asc' | 'desc' } | null) =>
    current &&
    tabSave.schedule(current, { ...current, sort: next as DatabaseSearchTabDto['sort'] });

  const handleColumnLayoutChange = (next: DatabaseTableProps['columnLayout']) =>
    current && tabSave.schedule(current, { ...current, columns: next });

  const searchesByRadius = Boolean(
    activeTab?.filters.zipCodes?.length && activeTab.filters.zipRadiusMiles !== undefined,
  );
  const companyFirst = useCompanyFirstSearch(
    selectedOrganization.id,
    searchesByRadius ? undefined : activeTab?.filters,
    sort && ['firstName', 'lastName', 'department'].includes(sort.field) ? null : sort,
  );
  const { companies, companyIds, pendingRows } = companyFirst;
  const hasNoCompanies = companyIds?.length === 0;

  const { mutate: touchSearch } = useTouchDatabaseSearchTab({
    mutation: { meta: { optimistic: 'searchId' } },
  });

  const searchQuery = useInfiniteQuery({
    queryKey: [
      getSearchDatabasePeopleUrl(selectedOrganization.id),
      activeTab?.filters,
      sort,
      companyIds,
    ],
    queryFn: async ({ pageParam, queryKey }) => {
      const { data: page } = await searchDatabasePeople(selectedOrganization.id, {
        filters: activeTab!.filters,
        sort,
        page: pageParam,
        companyIds,
      });

      if (pageParam === 1 && !queryClient.getQueryData(queryKey)) {
        touchSearch({
          organizationId: selectedOrganization.id,
          searchId,
          data: { lastCount: page.totalRows },
        });
      }
      return page;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page * lastPage.pageSize < lastPage.totalRows ? lastPage.page + 1 : undefined,
    enabled: Boolean(activeTab) && (searchesByRadius || Boolean(companyIds?.length)),
  });
  const { data, hasNextPage, fetchNextPage } = searchQuery;

  const isPendingPeople = searchQuery.isPending && Boolean(companyIds?.length);
  const sortField = sort?.field;
  const rows = useMemo(() => {
    const leadScores = new Map(companies?.map(({ companyId, leadScore }) => [companyId, leadScore]));
    const rank = new Map(
      sortField === 'leadScore' || sortField === 'intent'
        ? companies?.map(({ companyId }, index) => [companyId, index])
        : [],
    );
    return (
      data?.pages.flatMap((page) =>
        page.rows.map((row) => ({
          ...row,
          company: { ...row.company, leadScore: leadScores.get(row.companyId!) ?? null },
        })),
      ) ?? []
    ).sort((a, b) => (rank.get(a.companyId!) ?? rank.size) - (rank.get(b.companyId!) ?? rank.size));
  }, [data, companies, sortField]);
  const totalRows = data?.pages[0]?.totalRows ?? 0;

  useAiUiSnapshot({
    entity: 'database',
    searchId,
    filters: activeTab?.filters ?? {},
    databaseSort: sort,
    visibleCount: rows.length,
    total: totalRows,
    hasMore: hasNextPage,
    enabled: Boolean(activeTab),
  });

  const selectionKey = activeTab
    ? `${searchId}:${canonicalFiltersKey(activeTab.filters)}:${JSON.stringify(sort)}`
    : searchId;
  const selection = useTableSelection(selectionKey);
  const selectedCount = selection.count(totalRows);
  const selectedLoadedRows = rows.filter((row, index) =>
    selection.isSelected(row.employeeId, index),
  );
  const isSelectionRevealed =
    selectedLoadedRows.length === selectedCount &&
    selectedLoadedRows.every((row) => row.previouslyRevealed);
  const revealFirstAction = {
    disabled: !isSelectionRevealed,
    tooltip: isSelectionRevealed ? undefined : 'Reveal the selected people first',
  };
  const revealedTarget: BulkRecipientsBodyDto = {
    entityType: 'contact',
    scope: 'ids',
    ids: selectedLoadedRows.flatMap((row) => (row.contactId ? [row.contactId] : [])),
  };

  const [revealTarget, setRevealTarget] = useState<{
    selection: NonNullable<RevealPopoverProps['selection']>;
    notice: string | null;
    anchor: DOMRect;
  } | null>(null);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage({ cancelRefetch: false });
    }
  }, [hasNextPage, fetchNextPage]);

  const handlePersonClick = useCallback(
    (row: DatabaseSearchResponseDtoRowsItem) => setDrawer({ open: true, row, view: 'person' }),
    [],
  );

  const handleCompanyClick = useCallback(
    (row: DatabaseSearchResponseDtoRowsItem) => setDrawer({ open: true, row, view: 'company' }),
    [],
  );

  const handleCompose = useCallback(
    (channel: 'email' | 'sms', contactId: string) =>
      setCompose({ channel, target: { entityType: 'contact', scope: 'ids', ids: [contactId] } }),
    [],
  );

  const countCapNotice = `Only the first ${REVEAL_COUNT_MAX.toLocaleString()} people will be revealed — that is the most one reveal can take.`;

  const idsSelection = (employeeIds: string[], notice: string | null = null) => {
    const capped = employeeIds.slice(0, REVEAL_COUNT_MAX);
    return {
      selection: { kind: 'ids' as const, employeeIds: capped },
      notice:
        capped.length < employeeIds.length
          ? [countCapNotice, notice].filter(Boolean).join(' ')
          : notice,
    };
  };

  const buildRevealSelection = () => {
    if (selection.mode === 'ids') {
      return idsSelection(selection.includedIds);
    }

    const span =
      selection.mode === 'all'
        ? totalRows
        : Math.min(selection.selectAll.firstCount ?? 0, totalRows);
    const excluded = new Set(selection.excludedIds);
    const loadedIds = rows
      .slice(0, span)
      .filter((row) => !excluded.has(row.employeeId))
      .map((row) => row.employeeId);

    if (span <= rows.length) {
      return idsSelection(loadedIds);
    }

    if (!activeTab || excluded.size > REVEAL_EXCLUDE_MAX) {
      return idsSelection(
        loadedIds,
        `Too many rows are unticked to reveal the whole search — only the ${loadedIds.length.toLocaleString()} loaded, ticked ${
          loadedIds.length === 1 ? 'person' : 'people'
        } will be revealed.`,
      );
    }

    const count = Math.min(selectedCount, REVEAL_COUNT_MAX);
    return {
      selection: {
        kind: 'filters' as const,
        filters: activeTab.filters,
        sort,
        count,
        excludeEmployeeIds: selection.excludedIds,
      },
      notice: count < selectedCount ? countCapNotice : null,
    };
  };

  const patchRows = (
    patch: (row: DatabaseSearchResponseDtoRowsItem) => DatabaseSearchResponseDtoRowsItem,
  ) => {
    queryClient.setQueriesData<InfiniteData<DatabaseSearchResponseDto>>(
      { queryKey: [getSearchDatabasePeopleUrl(selectedOrganization.id)] },
      (previous) =>
        previous && {
          ...previous,
          pages: previous.pages.map((page) => ({ ...page, rows: page.rows.map(patch) })),
        },
    );
    setDrawer((previous) => previous && { ...previous, row: patch(previous.row) });
  };

  const refetchRows = () =>
    queryClient.invalidateQueries({
      queryKey: [getSearchDatabasePeopleUrl(selectedOrganization.id)],
    });

  const handleRevealStart: RevealPopoverProps['onRevealStart'] = (target, mode) => {
    const ids = new Set(
      target.kind === 'ids' ? target.employeeIds : selectedLoadedRows.map((row) => row.employeeId),
    );

    const [recordsNavItem] = findVisible('a[href="/records"]');
    if (recordsNavItem) {
      fly(
        [...ids]
          .flatMap((id) => findVisible(`[data-entity="contact:${id}"]`))
          .flatMap((avatar) => avatar.parentElement ?? [])
          .slice(0, 10)
          .map((source) => ({ source, target: recordsNavItem })),
      );
    }

    patchRows((row) =>
      ids.has(row.employeeId) && !row.previouslyRevealed
        ? { ...row, previouslyRevealed: mode === 'sync', revealPending: true }
        : row,
    );
    selection.clear();
  };

  const handleRevealed: RevealPopoverProps['onRevealed'] = (results) => {
    const resultsById = new Map(results.map((result) => [result.employeeId, result]));
    patchRows((row) => {
      const result = resultsById.get(row.employeeId);

      return result
        ? {
            ...row,
            previouslyRevealed: result.status === 'revealed',
            revealPending: false,
            email: result.email,
            phone: result.phone,
            leadId: result.leadId,
            contactId: result.contactId,
          }
        : row;
    });
    refetchRows();
    const revealed = results.filter((result) => result.status === 'revealed').length;
    message.success(`Revealed ${revealed} ${revealed === 1 ? 'person' : 'people'}`);
  };

  const handleReveal = (row: DatabaseSearchResponseDtoRowsItem, anchor: HTMLElement) =>
    setRevealTarget({
      ...idsSelection([row.employeeId]),
      anchor: anchor.getBoundingClientRect(),
    });

  if (!canSearch) {
    return <Result status="403" title="You do not have permission to search the database" />;
  }

  if (tabsQuery.isError) {
    return <Result status="error" title="Failed to load searches" />;
  }

  if (!activeTab) {
    return <Loader />;
  }

  return (
    <Flex vertical style={{ height: `calc(100dvh - ${insets.top}px)` }}>
      <Flex
        align="flex-end"
        justify="space-between"
        style={{
          padding: isMobile
            ? `8px 12px ${isSideNav ? 12 : 0}px`
            : isSideNav
              ? '16px 24px 8px 0'
              : '0 24px',
          minHeight: isMobile || isSideNav ? undefined : density === 'compact' ? 40 : 44,
          backgroundColor: 'var(--ant-color-bg-layout)',
        }}
      >
        {isSideNav && <GlobalSearch organizationId={selectedOrganization.id} />}
        <Flex align="flex-end" gap={6}>
          <ConfigProvider theme={toolbarTheme}>
            <DatabaseSearchTabs
              organizationId={selectedOrganization.id}
              tabs={openTabs ?? []}
              filterDefinitions={filtersQuery.data?.data.filters ?? []}
              activeSearchId={searchId}
            />
          </ConfigProvider>
          <AutosaveIndicator
            isSaving={tabSave.isSaving}
            canUndo={tabSave.canUndo}
            onUndo={tabSave.undo}
          />
        </Flex>
        <Flex align="center" gap={8} style={{ marginBottom: isSideNav ? 0 : 6 }}>
          {isSideNav && <HeaderActions organizationId={selectedOrganization.id} variant="side" />}
          <ColumnPickerButton
            groups={[
              {
                key: 'person',
                label: 'Person fields',
                items: [
                  { id: 'person', label: 'Person', locked: true },
                  { id: 'title', label: 'Title' },
                  { id: 'email', label: 'Email' },
                  { id: 'phone', label: 'Phone' },
                  { id: 'linkedin', label: 'LinkedIn' },
                  { id: 'revealedOn', label: 'Revealed on' },
                ],
              },
              {
                key: 'company',
                label: 'Company fields',
                items: [
                  { id: 'company', label: 'Company', locked: true },
                  { id: 'website', label: 'Website' },
                  { id: 'leadScore', label: 'Growth rate' },
                  { id: 'industry', label: 'Industry' },
                  { id: 'employees', label: 'Employees' },
                  { id: 'tags', label: 'Tags' },
                  { id: 'region', label: 'Region' },
                  { id: 'revenue', label: 'Annual revenue' },
                  { id: 'founded', label: 'Founded' },
                ],
              },
            ].map((group) => ({
              ...group,
              items: group.items.map((item) => ({
                ...item,
                checked: columnLayout[item.id]?.visible ?? true,
              })),
            }))}
            onItemCheckedChange={(columnId, checked) =>
              handleColumnLayoutChange({
                ...columnLayout,
                [columnId]: { ...columnLayout[columnId], visible: checked },
              })
            }
          />
        </Flex>
      </Flex>
      <TableSurface
        className={`table-surface--page-inset${isSideNav ? '' : ' table-surface--page-inset-top-nav'}`}
        testId="database-grid"
      >
        {companyFirst.isError || searchQuery.isError ? (
          <DatabaseSearchError
            error={companyFirst.error ?? searchQuery.error}
            failedAt={Math.max(companyFirst.errorUpdatedAt, searchQuery.errorUpdatedAt)}
            isRetrying={companyFirst.isFetching || searchQuery.isFetching}
            onRetry={() => (companyFirst.isError ? companyFirst.refetch() : searchQuery.refetch())}
            onRefine={() => {
              uiContext.setScopedFlow({ databaseSearchEditor: { searchId } });
              panel.newChat();
            }}
          />
        ) : hasNoCompanies ? (
          <Flex flex={1} align="center" justify="center">
            <Empty description="No results for this search yet — refine it with AI" />
          </Flex>
        ) : searchQuery.isPending && !isPendingPeople ? (
          <Loader />
        ) : (
          <DatabaseTable
            rows={isPendingPeople ? pendingRows : rows}
            pendingPeople={isPendingPeople}
            onPersonClick={handlePersonClick}
            onCompanyClick={handleCompanyClick}
            canReveal={canReveal}
            onReveal={handleReveal}
            canSendEmail={can('emails', 'send') && canSendEmail}
            canSendSms={can('sms', 'send') && canSendSms}
            onCompose={handleCompose}
            onLoadMore={handleLoadMore}
            sort={sort}
            onSortChange={handleSortChange}
            columnLayout={columnLayout}
            onColumnLayoutChange={handleColumnLayoutChange}
            selectedRows={selection.selectedRows}
            onSelectedRowsChange={selection.setSelectedRows}
            selectAll={selection.selectAll}
            totalRows={isPendingPeople ? pendingRows.length : totalRows}
          />
        )}
      </TableSurface>
      <DatabaseDrawer
        organizationId={selectedOrganization.id}
        organizationMembershipId={selectedMembership.id}
        open={drawer?.open ?? false}
        row={drawer?.row ?? null}
        view={drawer?.view ?? 'person'}
        canReveal={canReveal}
        onReveal={handleReveal}
        onOpen={(row, view) => setDrawer({ open: true, row, view })}
        onClose={() => setDrawer((previous) => previous && { ...previous, open: false })}
      />
      <RevealPopover
        organizationId={selectedOrganization.id}
        selection={revealTarget?.selection ?? null}
        anchor={revealTarget?.anchor ?? null}
        defaultSmartViewName={activeTab.name}
        onClose={() => setRevealTarget(null)}
        onRevealStart={handleRevealStart}
        onRevealed={handleRevealed}
        onRevealFailed={refetchRows}
      />
      <RevealJobToast organizationId={selectedOrganization.id} enabled={canReveal} />
      {compose && (
        <FloatingWindow storageKey="k3x_compose_window">
          {(dragHandle) =>
            compose.channel === 'email' ? (
              <EmailForm
                variant="card"
                organizationId={selectedOrganization.id}
                connectionId={emailConnection?.id ?? ''}
                emailConnections={connections}
                bulk={compose.target}
                dragHandle={dragHandle}
                onSuccess={() => {
                  setCompose(null);
                  selection.clear();
                }}
                onCancel={() => setCompose(null)}
              />
            ) : (
              <SmsForm
                variant="card"
                organizationId={selectedOrganization.id}
                organizationMembershipId={selectedMembership.id}
                bulk={compose.target}
                dragHandle={dragHandle}
                onSuccess={() => {
                  setCompose(null);
                  selection.clear();
                }}
                onCancel={() => setCompose(null)}
              />
            )
          }
        </FloatingWindow>
      )}
      <SelectionActionsBar
        entity="database"
        count={selectedCount}
        onAction={panel.newChat}
        onClear={selection.clear}
        actions={{
          reveal: {
            hidden: !canReveal,
            onClick: (event) =>
              setRevealTarget({
                ...buildRevealSelection(),
                anchor: event.currentTarget.getBoundingClientRect(),
              }),
          },
          agents: revealFirstAction,
          email: {
            hidden: !can('emails', 'send'),
            disabled: !isSelectionRevealed || !canSendEmail,
            tooltip: !isSelectionRevealed
              ? revealFirstAction.tooltip
              : canSendEmail
                ? undefined
                : 'Connect a mailbox',
            onClick: () => setCompose({ channel: 'email', target: revealedTarget }),
          },
          call: revealFirstAction,
          sms: {
            hidden: !can('sms', 'send'),
            disabled: !isSelectionRevealed || !canSendSms,
            tooltip: !isSelectionRevealed
              ? revealFirstAction.tooltip
              : canSendSms
                ? undefined
                : 'Set up a phone number',
            onClick: () => setCompose({ channel: 'sms', target: revealedTarget }),
          },
        }}
      />
    </Flex>
  );
};

export const Route = createFileRoute('/_authenticated/_app/database/$searchId')({
  component: DatabaseSearchRoute,
});
